/**
 * Reading Fansly's messaging payloads for the Chats page
 * (docs/fansly_crm_behaviour_spec.md §6–§8, checked against Fansly's own web
 * app): the chat list (`messaging/groups`), a thread (`message`), the chat's
 * media offers (`mediaoffers/location`), per-fan earnings and lists.
 * Pure functions: the hooks and components build on these.
 */
import {
  accountOf,
  accountsIn,
  aggregation,
  asList,
  asObj,
  attachmentsOf,
  byId,
  catalogOf,
  mergeCatalogs,
  num,
  str,
  toMs,
  unwrap,
  usd,
  type Attachment,
  type FanslyAccount,
  type MediaCatalog,
  type Raw,
} from '../data'

// ── Segments ────────────────────────────────────────────────────────────────

export type Segment = 'recent' | 'unread' | 'followers' | 'subscribers' | 'following'

/**
 * Fansly's own client: sort 1 = newest, 3 = unread; flags 2 = follows you,
 * 4 = subscribers, 8 = accounts this creator follows (its SORT_MODES and tab
 * flags). The old workspace sent sort 0/1, which Fansly reads as newest.
 */
export const SEGMENTS: Record<Segment, { sortOrder: number; flags: number }> = {
  recent: { sortOrder: 1, flags: 0 },
  unread: { sortOrder: 3, flags: 0 },
  followers: { sortOrder: 1, flags: 2 },
  subscribers: { sortOrder: 1, flags: 4 },
  following: { sortOrder: 1, flags: 8 },
}

export const SEGMENT_ORDER: Segment[] = ['recent', 'unread', 'followers', 'subscribers', 'following']

/** Chats asked per page (Fansly's own client pages by 20). */
export const CHAT_PAGE = 20

// ── Chat list ───────────────────────────────────────────────────────────────

export type PreviewKind = 'text' | 'media' | 'tip' | 'gif' | 'voice' | 'new' | 'empty'

export interface ChatPreview {
  kind: PreviewKind
  text: string
}

export interface ChatRow {
  groupId: string
  /** The fan's Fansly account id. */
  fanId: string
  username: string
  /** Display name, else @username, else ''. */
  name: string
  avatar: string
  account: FanslyAccount | null
  unread: number
  preview: ChatPreview
  /** The last message is this creator's own. */
  fromMe: boolean
  /** Last activity (ms). */
  time: number
  lastMessageId: string
  subscriber: boolean
  followsYou: boolean
  /** Fansly's per-chat read receipts: 2 on, 1 off, 0 the account default. */
  readReceipts: number
  /** The fan's ignore flags as this creator set them (1 muted, 2 blocked). */
  ignoring: number
  raw: Raw
}

const PS_CONTENT = /content=([^;}]*)/

/** The text of a last message in any of the shapes the old workspace met (object, JSON string, PowerShell dump). */
export function messageText(m: unknown): string {
  if (typeof m === 'string') {
    const s = m.trim()
    if (!s) return ''
    if (/^\d{8,}$/.test(s)) return ''
    if (s.startsWith('{')) {
      try {
        return messageText(JSON.parse(s))
      } catch {
        return s
      }
    }
    if (s.startsWith('@{')) return (PS_CONTENT.exec(s)?.[1] ?? '').trim()
    return s
  }
  const o = asObj(m)
  for (const k of ['content', 'text', 'message', 'body']) {
    const v = o[k]
    if (typeof v === 'string') return v.trim()
    if (v && typeof v === 'object') return messageText(v)
  }
  return ''
}

/** A Fansly GIF: the message is the provider's share URL (Fansly turns it into an embed). */
export const isGifUrl = (s: string) => /^https?:\/\/(?:[\w-]+\.)*(klipy\.com|tenor\.com|giphy\.com)\//i.test(s.trim())

/** A tip rides on a message as an attachment of content type 7 (or `messageTip`). */
const TIP_TYPE = 7

function previewOf(last: Raw | null, unread: number): ChatPreview {
  if (last) {
    const text = messageText(last)
    if (text && isGifUrl(text)) return { kind: 'gif', text: '' }
    if (text) return { kind: 'text', text }
    const atts = asList(last.attachments)
    if (last.messageTip || atts.some((a) => num(a.contentType) === TIP_TYPE)) return { kind: 'tip', text: '' }
    if (asList(last.embeds).length) return { kind: 'gif', text: '' }
    if (atts.length || last.accountMediaId || last.accountMediaBundleId) return { kind: 'media', text: '' }
  }
  return unread > 0 ? { kind: 'new', text: '' } : { kind: 'empty', text: '' }
}

/** The fan in a group: the row's partner, else the group member that is not this creator. */
function partnerOf(row: Raw, group: Raw, meId: string): string {
  const direct = str(row.partnerAccountId) || str(row.partner_account_id) || str(row.accountId) || str(asObj(row.account).id)
  if (direct) return direct
  const users = asList(group.users)
  const other = users.find((u) => str(u.userId) && str(u.userId) !== meId && str(u.userId) !== str(group.createdBy))
  return str(other?.userId) || str(users.find((u) => str(u.userId) !== meId)?.userId)
}

/** `messaging/groups` → rows, joined to their accounts and groups; broadcast groups (type 3) are left out. */
export function chatRowsOf(payload: unknown, meId = ''): ChatRow[] {
  const un = unwrap(payload)
  const u = asObj(un)
  const agg = aggregation(payload)
  const accounts = accountsIn(payload)
  const groups = byId(asList(agg.groups))
  const list = Array.isArray(un) ? asList(un) : asList(u.data ?? u.groups ?? u.list ?? u.items)
  const out: ChatRow[] = []
  const seen = new Set<string>()
  for (const row of list) {
    const groupId = str(row.groupId) || str(row.id) || str(row.group_id)
    if (!groupId || seen.has(groupId)) continue
    const group = groups.get(groupId) ?? {}
    if (num(group.type) === 3 || num(row.type) === 3) continue
    seen.add(groupId)
    const fanId = partnerOf(row, group, meId)
    const account = accounts.get(fanId) ?? null
    const rowLast = asObj(row.lastMessage)
    const groupLast = asObj(group.lastMessage)
    const last = Object.keys(rowLast).length ? rowLast : Object.keys(groupLast).length ? groupLast : null
    const unread = num(row.unreadCount ?? row.unread_count)
    const username = str(row.partnerUsername) || account?.username || ''
    out.push({
      groupId,
      fanId,
      username,
      name: account?.name || str(row.partnerDisplayName) || username,
      avatar: account?.avatar ?? '',
      account,
      unread,
      preview: previewOf(last, unread),
      fromMe: !!last && !!fanId && str(last.senderId) !== '' && str(last.senderId) !== fanId,
      time: toMs(last?.createdAt) || toMs(row.lastMessageAt) || toMs(row.updatedAt) || toMs(group.createdAt) || toMs(row.createdAt),
      lastMessageId: str(last?.id) || str(row.lastMessageId),
      subscriber: !!str(row.subscriptionTierId) || account?.subscriber === true,
      followsYou: account?.followsYou === true,
      readReceipts: num(group.sendReadReceipts ?? row.sendReadReceipts),
      ignoring: num(account?.raw.ignoring),
      raw: { ...row, group },
    })
  }
  return out
}

/**
 * The list after another page or a poll: rows already shown keep their place
 * unless the new answer moved them; `active` keeps 0 unread (it is open).
 */
export function mergeChatRows(prev: ChatRow[], incoming: ChatRow[], opts: { prepend?: boolean; active?: string | null } = {}): ChatRow[] {
  const fresh = new Map(incoming.map((r) => [r.groupId, r]))
  const keepPreview = (old: ChatRow | undefined, next: ChatRow): ChatRow => {
    let r = next
    if (old && next.preview.kind === 'empty' && old.preview.kind !== 'empty') r = { ...r, preview: old.preview, fromMe: old.fromMe, time: Math.max(old.time, r.time) }
    if (opts.active && r.groupId === opts.active) r = { ...r, unread: 0 }
    return r
  }
  const oldById = new Map(prev.map((r) => [r.groupId, r]))
  if (opts.prepend) {
    // A poll of the first page: its rows lead, in Fansly's order; the rest stay below.
    const head = incoming.map((r) => keepPreview(oldById.get(r.groupId), r))
    return [...head, ...prev.filter((r) => !fresh.has(r.groupId))]
  }
  const updated = prev.map((r) => (fresh.has(r.groupId) ? keepPreview(r, fresh.get(r.groupId)!) : r))
  const added = incoming.filter((r) => !oldById.has(r.groupId)).map((r) => keepPreview(undefined, r))
  return [...updated, ...added]
}

/** Recent: chats with a conversation first, then the latest activity. */
export function sortRecent(rows: ChatRow[]): ChatRow[] {
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => Number(b.r.preview.kind !== 'empty') - Number(a.r.preview.kind !== 'empty') || b.r.time - a.r.time || a.i - b.i)
    .map((x) => x.r)
}

// ── Messages ────────────────────────────────────────────────────────────────

export interface ChatTip {
  /** Dollars. */
  amount: number
}

export interface ChatGif {
  url: string
  /** The image to show (tinygif / gif). */
  src: string
  width: number
  height: number
  title: string
}

export interface ChatLike {
  id: string
  accountId: string
  type: number
}

export interface ChatMessage {
  id: string
  groupId: string
  senderId: string
  /** This creator's own message. */
  mine: boolean
  content: string
  /** ms. */
  createdAt: number
  attachments: Attachment[]
  tip: ChatTip | null
  gif: ChatGif | null
  likes: ChatLike[]
  /** The fan has read it (Fansly's interactions). */
  seen: boolean
  deleted: boolean
  inReplyTo: string
  raw: Raw
}

/** Snowflake ids as numbers can't be compared: by length, then text. */
export function compareIds(a: string, b: string): number {
  if (a.length !== b.length) return a.length - b.length
  return a < b ? -1 : a > b ? 1 : 0
}

function gifOf(m: Raw): ChatGif | null {
  for (const e of asList(m.embeds)) {
    let data: Raw = {}
    try {
      data = asObj(typeof e.data === 'string' ? JSON.parse(e.data) : e.data)
    } catch {
      data = {}
    }
    const variants = asList(data.variants)
    const pick = ['tinygif', 'gif', 'nanogif'].map((f) => variants.find((v) => str(v.format) === f)).find(Boolean)
    const src = str(pick?.url) || str(data.tinyGifUrl) || str(data.gifUrl)
    if (src) return { url: str(data.itemurl) || str(data.url), src, width: num(pick?.width), height: num(pick?.height), title: str(data.title) }
  }
  return null
}

/** Tips the payload carries (`response.tips`), by id. */
function tipsOf(payload: unknown): Map<string, Raw> {
  const u = asObj(unwrap(payload))
  return byId([...asList(u.tips), ...asList(asObj(u.aggregationData).tips)])
}

function tipOf(m: Raw, tips: Map<string, Raw>): ChatTip | null {
  const direct = num(asObj(m.messageTip).amount)
  if (direct > 0) return { amount: usd(direct) }
  for (const a of asList(m.attachments)) {
    if (num(a.contentType) !== TIP_TYPE) continue
    const tip = tips.get(str(a.contentId))
    if (tip && num(tip.amount) > 0) return { amount: usd(tip.amount) }
  }
  const total = num(m.totalTipAmount ?? m.tipAmount)
  return total > 0 ? { amount: usd(total) } : null
}

export function messageOf(raw: Raw, ctx: { fanId: string; meId: string; catalog: MediaCatalog; tips?: Map<string, Raw> }): ChatMessage {
  const senderId = str(raw.senderId) || str(raw.accountId)
  const mine = senderId ? (ctx.fanId ? senderId !== ctx.fanId : senderId === ctx.meId) : raw.isOwn === true
  const interactions = asList(raw.interactions)
  const content = messageText(raw)
  const gif = gifOf(raw)
  return {
    id: str(raw.id) || str(raw.messageId),
    groupId: str(raw.groupId),
    senderId,
    mine,
    // A GIF is sent as its share URL; the embed shows it.
    content: gif && content.trim() === gif.url ? '' : content,
    createdAt: toMs(raw.createdAt ?? raw.created_at),
    attachments: attachmentsOf(raw, ctx.catalog),
    tip: tipOf(raw, ctx.tips ?? new Map()),
    gif,
    likes: asList(raw.likes).map((l) => ({ id: str(l.id), accountId: str(l.accountId), type: num(l.type) || 1 })),
    seen: mine && interactions.some((i) => str(i.userId) === ctx.fanId && num(i.readAt) > 0),
    deleted: raw.deleted === true || num(raw.deletedAt) > 0,
    inReplyTo: str(raw.inReplyTo),
    raw,
  }
}

export interface ThreadPage {
  messages: ChatMessage[]
  /** The media and bundles this page carries (later pages may refer back). */
  catalog: MediaCatalog
  /** Raw rows received (0 = the end). */
  received: number
  /** Offset mode: Fansly advertised the next offset. */
  nextOffset: number | null
  /** The explicit next cursor, else ''. */
  nextCursor: string
  hasMoreFlag: boolean | null
}

/** One `message` answer, resolved against its own catalog plus what earlier pages carried. */
export function threadPageOf(payload: unknown, ctx: { fanId: string; meId: string; catalog?: MediaCatalog }): ThreadPage {
  const u = asObj(unwrap(payload))
  const own = catalogOf(payload)
  const catalog = ctx.catalog ? mergeCatalogs(ctx.catalog, own) : own
  const list = asList(u.messages ?? u.data ?? u.list ?? u.items)
  const tips = tipsOf(payload)
  const messages = list.map((m) => messageOf(m, { fanId: ctx.fanId, meId: ctx.meId, catalog, tips })).filter((m) => m.id)
  const pagination = asObj(u.pagination)
  const nextOffset = u.nextOffset ?? pagination.nextOffset
  const cursor = str(u.nextCursor) || str(u.next_cursor) || str(pagination.nextCursor)
  const more = u.hasMore ?? pagination.hasMore
  return {
    messages,
    catalog: own,
    received: list.length,
    nextOffset: nextOffset == null || nextOffset === '' ? null : num(nextOffset),
    nextCursor: cursor,
    hasMoreFlag: typeof more === 'boolean' ? more : null,
  }
}

/** De-duplicated by id, oldest first (time, then id). */
export function normalizeMessages(list: ChatMessage[]): ChatMessage[] {
  const byKey = new Map<string, ChatMessage>()
  for (const m of list) byKey.set(m.id, m)
  return [...byKey.values()].sort((a, b) => a.createdAt - b.createdAt || compareIds(a.id, b.id))
}

/** The query for one thread request (§7.4): a reset, an older page (cursor or offset mode) or a poll. */
export function threadQuery(groupId: string, mode: { kind: 'reset' } | { kind: 'older'; before: string; offset: number | null } | { kind: 'poll'; after: string }, limit = 50) {
  const q: Record<string, string | number> = { groupId, limit, offset: 0 }
  if (mode.kind === 'older') {
    if (mode.offset != null) {
      q.offset = mode.offset
    } else {
      // Cursor mode: the oldest loaded id with offset 0 (a non-zero offset with `before` returns nothing).
      for (const k of ['before', 'message_id', 'marker_id', 'marker_message_id', 'last_message_id']) q[k] = mode.before
    }
  } else if (mode.kind === 'poll') {
    q.after = mode.after
  }
  return q
}

/** Liked by this creator (Fansly like type 1). */
export const likedByMe = (m: ChatMessage, meId: string) => m.likes.some((l) => l.type === 1 && (meId ? l.accountId === meId : l.accountId !== m.senderId))
/** The fan liked this creator's message. */
export const likedByFan = (m: ChatMessage, fanId: string) => m.likes.some((l) => l.accountId === fanId)

// ── Media offers (the chat's gallery) ───────────────────────────────────────

/** Fansly's location type for a group's media offers (GroupService 4 × 1000 + 1). */
export const GROUP_MEDIA_LOCATION = 4001

export interface OfferPage {
  items: Attachment[]
  /** The id to pass as `before` for the next page, '' at the end. */
  next: string
}

/** `mediaoffers/location` → the attachments it offers, joined from aggregationData. */
export function offersOf(payload: unknown, limit: number): OfferPage {
  const u = asObj(unwrap(payload))
  const catalog = catalogOf(payload)
  const rowsList = asList(u.data ?? u.mediaOffers ?? u.list)
  const items: Attachment[] = []
  const seen = new Set<string>()
  for (const r of rowsList) {
    const bundleId = str(r.mediaOfferBundleId)
    const id = str(r.mediaOfferId) || str(r.accountMediaId)
    const fake = bundleId && catalog.bundles.has(bundleId) ? { accountMediaBundleId: bundleId } : { accountMediaId: id }
    for (const a of attachmentsOf(fake, catalog)) {
      const key = a.type === 'bundle' ? `b${a.bundle.id}` : `m${a.item.id}`
      if (seen.has(key)) continue
      seen.add(key)
      items.push(a)
    }
  }
  const last = rowsList[rowsList.length - 1]
  return { items, next: rowsList.length >= limit && last ? str(last.id) : '' }
}

// ── Per-fan earnings ────────────────────────────────────────────────────────

export type SpendKind = 'tips' | 'subscriptions' | 'media' | 'bundles' | 'locked' | 'other'

/** Fansly's earnings types (legacy and current) → what the operator reads. */
const SPEND_TYPES: Record<number, SpendKind> = {
  7001: 'tips',
  7101: 'tips',
  15001: 'subscriptions',
  2010: 'media',
  2110: 'media',
  2016: 'bundles',
  2116: 'bundles',
  32001: 'locked',
  32101: 'locked',
}

export interface FanSpend {
  /** Dollars, gross. */
  total: number
  net: number
  byKind: Partial<Record<SpendKind, number>>
}

export function spendOf(rowsList: Raw[]): FanSpend {
  // Summed in Fansly's thousandths, so the dollars come out exact.
  let total = 0
  let net = 0
  const kinds: Partial<Record<SpendKind, number>> = {}
  for (const r of rowsList) {
    const gross = num(r.totalGross)
    total += gross
    net += num(r.totalNet)
    const kind = SPEND_TYPES[num(r.type)] ?? 'other'
    kinds[kind] = (kinds[kind] ?? 0) + gross
  }
  const byKind: Partial<Record<SpendKind, number>> = {}
  for (const [k, v] of Object.entries(kinds)) byKind[k as SpendKind] = usd(v)
  return { total: usd(total), net: usd(net), byKind }
}

// ── Lists ───────────────────────────────────────────────────────────────────

export interface FanList {
  id: string
  name: string
  count: number
  /** The fan is on it (lists/account?itemId=<fan> carries the fan in `items`). */
  member: boolean
}

/** Custom lists (type 1) from `lists/account?itemId=<fan>`, with the fan's membership. */
export function fanListsOf(payload: unknown, fanId: string): FanList[] {
  const list = asList(Array.isArray(unwrap(payload)) ? unwrap(payload) : asObj(unwrap(payload)).lists)
  return list
    .filter((l) => str(l.id) && (l.type == null || num(l.type) === 1))
    .map((l, i) => ({
      id: str(l.id),
      name: str(l.label) || str(l.name) || str(l.title) || `#${i + 1}`,
      count: num(l.itemCount),
      member:
        asList(l.items).some((it) => str(it.id) === fanId || str(it.accountId) === fanId) ||
        ['hasUser', 'isAdded', 'inList', 'added', 'containsItem'].some((k) => l[k] === true),
    }))
}

// ── Notes ───────────────────────────────────────────────────────────────────

/** Fansly's note on an account (AccountService 12 × 1000). */
export const ACCOUNT_NOTE = 12000

export interface FanNote {
  id: string
  title: string
  note: string
  createdAt: number
  raw: Raw
}

/** The creator's notes on the fan, from the fan's account (`notes`, account-note type only). */
export function notesOf(account: Raw | null | undefined): FanNote[] {
  return asList(asObj(account).notes)
    .filter((n) => num(n.contentType) === ACCOUNT_NOTE)
    .map((n) => ({ id: str(n.id), title: str(n.title), note: str(n.note), createdAt: toMs(n.createdAt), raw: n }))
    .filter((n) => n.id)
}

export function noteFromAnswer(payload: unknown, fallback: { title: string; note: string; fanId: string }): FanNote {
  const n = asObj(unwrap(payload))
  return {
    id: str(n.id) || `local-${Date.now().toString(36)}`,
    title: str(n.title) || fallback.title,
    note: str(n.note) || fallback.note,
    createdAt: toMs(n.createdAt) || Date.now(),
    raw: Object.keys(n).length ? n : { contentType: ACCOUNT_NOTE, contentId: fallback.fanId, title: fallback.title, note: fallback.note },
  }
}

export { accountOf }
